import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface TaskComment {
  id: string;
  task_id: string;
  company_id: string;
  created_by: string;
  content: string;
  created_at: string;
  author?: { id: string; name: string; email: string };
}

export function useTaskComments(taskId: string | undefined) {
  const { profile } = useAuth();
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (!taskId || !profile?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await (supabase as any)
        .from("compliance_task_comments")
        .select("*, author:profiles!compliance_task_comments_created_by_fkey(id, name, email)")
        .eq("task_id", taskId)
        .order("created_at", { ascending: true });

      if (fetchError) throw fetchError;
      setComments(
        (data || []).map((c: any) => ({ ...c, author: c.author || undefined }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [taskId, profile?.company_id]);

  useEffect(() => {
    if (!taskId || !profile?.company_id) {
      setLoading(false);
      return;
    }

    fetchComments();

    const channel = supabase
      .channel(`task-comments-${taskId}`)
      .on(
        "postgres_changes" as any,
        {
          event: "*",
          schema: "public",
          table: "compliance_task_comments",
          filter: `task_id=eq.${taskId}`,
        },
        () => fetchComments()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, profile?.company_id]);

  const addComment = async (content: string) => {
    if (!taskId || !profile?.id || !profile?.company_id) throw new Error("Not authenticated");

    const { error: insertError } = await (supabase as any)
      .from("compliance_task_comments")
      .insert({
        task_id: taskId,
        company_id: profile.company_id,
        created_by: profile.id,
        content: content.trim(),
      });

    if (insertError) throw insertError;
    await fetchComments();
  };

  const deleteComment = async (commentId: string) => {
    const { error: deleteError } = await (supabase as any)
      .from("compliance_task_comments")
      .delete()
      .eq("id", commentId);

    if (deleteError) throw deleteError;
    await fetchComments();
  };

  const canDelete = (comment: TaskComment) => {
    if (!profile) return false;
    return comment.created_by === profile.id || (profile as any).role === "admin";
  };

  return { comments, loading, error, addComment, deleteComment, canDelete };
}
